import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { getChatRooms, getRoomMessages, sendRoomMessage } from '../../api/chat';
import chatSocket from '../../api/chat-socket';
import MessageConversationList from '../../components/Messaging/MessageConversationList';
import MessageDetailView from '../../components/Messaging/MessageDetailView';
import useThreadHistory from '../../components/Messaging/useThreadHistory';
import LoadingState from '../../components/LoadingState/LoadingState';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import InlineEmpty from '../../components/InlineEmpty/InlineEmpty';
import SearchInput from '../../components/SearchInput/SearchInput';
import { useHideBottomNav } from '../../components/Layout/shellState';
import { useI18n } from '../../i18n/I18nContext';
import './DirectMessaging.css';

const MESSAGES_PAGE_SIZE = 50;
// Room-list refreshes triggered by socket events are batched: a burst of messages (or the
// echo of our own) costs one request, and each room costs the API two queries.
const ROOMS_REFRESH_DELAY_MS = 400;
// A socket that is down for a moment (every page load starts that way) is not worth a notice.
const OFFLINE_NOTICE_DELAY_MS = 2500;

// A direct room is named `dm:{userA}:{userB}` (ids sorted; older rooms `dm:{recipientId}`): the
// other person is whichever id is not the signed-in staff member's.
function peerIdOf(roomId, currentUserId) {
  const ids = String(roomId || '').split(':').slice(1).filter(Boolean);
  const others = currentUserId ? ids.filter((id) => id !== currentUserId) : ids;
  return others.length === 1 ? others[0] : null;
}

function roomToConversation(room, currentUserId, t) {
  const participantName = room.name || room.otherParticipantName || t('messaging.unknownUser');
  // GET /chat/rooms sends lastMessage as { id, senderId, contentPreview, createdAt }; older
  // shapes were a plain string or { body, content, text }. Normalize to a string so
  // downstream rendering (substring, length) is always safe.
  const rawLast = room.lastMessageBody ?? room.lastMessage;
  const lastMessage =
    typeof rawLast === 'string'
      ? rawLast
      : (rawLast?.contentPreview || rawLast?.body || rawLast?.content || rawLast?.text || '');
  return {
    ...room,
    participantName,
    lastMessage,
    lastMessageAt: room.lastMessageAt || rawLast?.createdAt,
    unreadCount: room.unreadCount || 0,
    isOnline: room.isOtherParticipantOnline || false,
    // The API names a room `roomId`; the list, the selection and the sockets key on `id`.
    id: room.roomId ?? room.id,
    peerId: peerIdOf(room.roomId ?? room.id, currentUserId),
  };
}

// GET /chat/rooms lists every room the user is in, course chats included, and takes no filter.
function isDirectRoom(room) {
  return room.type ? room.type === 'dm' : String(room.id || '').startsWith('dm:');
}

function messageToViewShape(msg, currentUserId, t) {
  return {
    ...msg,
    isFromMe: msg.userId === currentUserId || msg.senderId === currentUserId,
    senderName: msg.senderName || msg.sender?.name || t('messaging.unknownSender'),
    content: msg.body || msg.content || '',
    createdAt: msg.createdAt,
  };
}

// The API pages messages newest-first, but the thread reads, scrolls and appends
// oldest-first. Merging by id also absorbs a message that arrives twice (our own send
// comes back over the socket) and puts it in its place by time.
function mergeMessages(...lists) {
  const byId = new Map();
  lists.forEach((list) => list.forEach((m) => byId.set(m.id, m)));
  return [...byId.values()].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
}

/**
 * The inbox's chat: one card, the conversation list on the left, the open thread on the right
 * (below 1024px one of the two at a time). `onThreadOpenChange(open)` tells the page around it
 * when a thread is open, so a phone can give the whole screen to it; `onUnreadChange(count)` how
 * many conversations have something unread, for the tab that leads here.
 */
function DirectMessaging({ onThreadOpenChange, onUnreadChange }) {
  const { t } = useI18n();
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesFailed, setMessagesFailed] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [wsConnected, setWsConnected] = useState(chatSocket.connected);
  const [showOffline, setShowOffline] = useState(false);

  const currentUserId = (() => {
    try {
      const token = sessionStorage.getItem('accessToken') || localStorage.getItem('accessToken');
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.sub || payload.userId || null;
    } catch {
      return null;
    }
  })();

  const selectedRoomRef = useRef(selectedRoom);
  useEffect(() => { selectedRoomRef.current = selectedRoom; }, [selectedRoom]);
  const roomsRequestRef = useRef(0);       // only the newest room-list response is applied
  const roomsRefreshTimerRef = useRef(null);
  const unsentReadRef = useRef(null);      // { roomId, messageId } that could not go out yet

  // A phone gives the whole screen to an open thread: the page header and the tab bar step aside.
  const threadOpen = Boolean(selectedRoom);
  const threadOpenChangeRef = useRef(onThreadOpenChange);
  threadOpenChangeRef.current = onThreadOpenChange;
  useEffect(() => {
    if (threadOpenChangeRef.current) threadOpenChangeRef.current(threadOpen);
  }, [threadOpen]);
  useEffect(() => () => {
    if (threadOpenChangeRef.current) threadOpenChangeRef.current(false);
  }, []);
  useHideBottomNav(threadOpen);

  const unreadChats = rooms.filter((r) => r.unreadCount > 0).length;
  const unreadChangeRef = useRef(onUnreadChange);
  unreadChangeRef.current = onUnreadChange;
  useEffect(() => {
    if (unreadChangeRef.current) unreadChangeRef.current(unreadChats);
  }, [unreadChats]);

  const fetchRooms = useCallback(async () => {
    const request = ++roomsRequestRef.current;
    try {
      setLoading(true);
      const result = await getChatRooms();
      if (request !== roomsRequestRef.current) return;
      const rawRooms = Array.isArray(result) ? result : result.rooms || result.data || [];
      setRooms(rawRooms.map((r) => roomToConversation(r, currentUserId, t)).filter(isDirectRoom));
      setListError(false);
    } catch (err) {
      console.error('[DirectMessaging] Failed to fetch conversations:', err);
      if (request === roomsRequestRef.current) setListError(true);
    } finally {
      if (request === roomsRequestRef.current) setLoading(false);
    }
  }, [currentUserId, t]);

  const refreshRoomsSoon = useCallback(() => {
    clearTimeout(roomsRefreshTimerRef.current);
    roomsRefreshTimerRef.current = setTimeout(fetchRooms, ROOMS_REFRESH_DELAY_MS);
  }, [fetchRooms]);

  const clearUnread = useCallback((roomId) => {
    setRooms((prev) => prev.map((r) => (
      r.id === roomId && r.unreadCount ? { ...r, unreadCount: 0 } : r
    )));
  }, []);

  // Tell the gateway the newest message of the open room has been seen. The only way to say
  // so is the socket; if it is down, remember the message and send it once it is back.
  const markRoomRead = useCallback((roomId, newestMessage) => {
    if (!newestMessage?.id) return;
    if (chatSocket.emitMarkRead(roomId, newestMessage.id)) {
      unsentReadRef.current = null;
      clearUnread(roomId);
    } else {
      unsentReadRef.current = { roomId, messageId: newestMessage.id };
    }
  }, [clearUnread]);

  const fetchMessages = useCallback(async (roomId) => {
    if (!roomId) return;
    setMessagesLoading(true);
    setMessagesFailed(false);
    try {
      const result = await getRoomMessages(roomId, { limit: MESSAGES_PAGE_SIZE });
      if (selectedRoomRef.current?.id !== roomId) return; // the agent has moved on
      const rawMsgs = Array.isArray(result) ? result : result.messages || result.data || [];
      const loaded = mergeMessages(
        [...rawMsgs].reverse().map((m) => messageToViewShape(m, currentUserId, t)),
      );
      // Keep what the socket delivered while this page was on its way.
      setMessages((prev) => mergeMessages(loaded, prev));
      setNextCursor(Array.isArray(result) ? null : result.nextCursor || null);
      setMessagesFailed(false);
      markRoomRead(roomId, loaded[loaded.length - 1]);
    } catch (err) {
      console.error('[DirectMessaging] Failed to fetch messages:', err);
      if (selectedRoomRef.current?.id === roomId) setMessagesFailed(true);
    } finally {
      // Only the open conversation's answer ends the loading: one the agent has left is not theirs.
      if (selectedRoomRef.current?.id === roomId) setMessagesLoading(false);
    }
  }, [currentUserId, t, markRoomRead]);

  const handleLoadOlder = async () => {
    const room = selectedRoomRef.current;
    if (!room || !nextCursor || loadingOlder) return;
    setLoadingOlder(true);
    try {
      const result = await getRoomMessages(room.id, { limit: MESSAGES_PAGE_SIZE, cursor: nextCursor });
      if (selectedRoomRef.current?.id !== room.id) return;
      const older = (result.messages || []).map((m) => messageToViewShape(m, currentUserId, t));
      setMessages((prev) => mergeMessages(older, prev));
      setNextCursor(result.nextCursor || null);
    } catch (err) {
      console.error('[DirectMessaging] Failed to load earlier messages:', err);
      toast.error(t('messaging.loadMessagesFailed'));
    } finally {
      setLoadingOlder(false);
    }
  };

  // The room list does not depend on the search box: the API takes no filter, so the
  // search narrows the loaded list below instead of fetching again on every keystroke.
  useEffect(() => { fetchRooms(); }, [fetchRooms]);

  // One socket for as long as the inbox is open.
  useEffect(() => {
    chatSocket.connect();
    return () => {
      clearTimeout(roomsRefreshTimerRef.current);
      chatSocket.disconnect();
    };
  }, []);

  useEffect(() => {
    setMessages([]);
    setNextCursor(null);
    setMessagesFailed(false);
    if (selectedRoom) fetchMessages(selectedRoom.id);
  }, [selectedRoom, fetchMessages]);

  // The "reconnecting" notice only appears when the socket stays down for a while.
  useEffect(() => {
    if (wsConnected) {
      setShowOffline(false);
      return undefined;
    }
    const timer = setTimeout(() => setShowOffline(true), OFFLINE_NOTICE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [wsConnected]);

  useEffect(() => {
    const handleReady = () => {
      setWsConnected(true);
      const pending = unsentReadRef.current;
      if (pending && pending.roomId === selectedRoomRef.current?.id) {
        markRoomRead(pending.roomId, { id: pending.messageId });
      }
    };
    const handleDown = () => setWsConnected(false);

    const handleNewMessage = (msg) => {
      // Only direct conversations are listed; course chats arrive here too for enrolled staff.
      if (!String(msg.roomId || '').startsWith('dm:')) return;
      const openRoom = selectedRoomRef.current;
      if (openRoom && msg.roomId === openRoom.id) {
        const incoming = messageToViewShape(msg, currentUserId, t);
        setMessages((prev) => mergeMessages(prev, [incoming]));
        if (!incoming.isFromMe) markRoomRead(openRoom.id, incoming);
      }
      refreshRoomsSoon();
    };

    // Messages for a conversation that is not open arrive on the user's own channel.
    const handleNewDM = ({ roomId, message }) => {
      if (message) handleNewMessage({ ...message, roomId });
      else refreshRoomsSoon();
    };

    const handleTyping = ({ roomId, userId, isTyping: typing }) => {
      const openRoom = selectedRoomRef.current;
      if (openRoom && roomId === openRoom.id && userId !== currentUserId) {
        clearTimeout(handleTyping._clearTimer);
        if (typing === false) {
          setIsTyping(false);
          return;
        }
        setIsTyping(true);
        handleTyping._clearTimer = setTimeout(() => setIsTyping(false), 3000);
      }
    };

    // The broadcast is the whole message row, so the new text is `content`.
    const handleEdited = (updated) => {
      const openRoom = selectedRoomRef.current;
      if (!updated?.id || (updated.roomId && updated.roomId !== openRoom?.id)) return;
      setMessages((prev) => prev.map((m) => (
        m.id === updated.id
          ? {
            ...m,
            content: updated.content ?? updated.body ?? m.content,
            editedAt: updated.editedAt ?? updated.updatedAt ?? m.editedAt,
          }
          : m
      )));
      refreshRoomsSoon();
    };

    // Deletes are announced as { messageId, roomId }.
    const handleDeleted = (payload) => {
      const { messageId, id, roomId } = payload || {};
      const deletedId = messageId ?? id;
      const openRoom = selectedRoomRef.current;
      if (!deletedId || (roomId && roomId !== openRoom?.id)) return;
      setMessages((prev) => prev.filter((m) => m.id !== deletedId));
      refreshRoomsSoon();
    };

    chatSocket.on('connected', handleReady);
    chatSocket.on('disconnect', handleDown);
    chatSocket.on('connect_error', handleDown);
    chatSocket.on('chat:message', handleNewMessage);
    chatSocket.on('newDM', handleNewDM);
    chatSocket.on('chat:typing', handleTyping);
    chatSocket.on('chat:messageEdited', handleEdited);
    chatSocket.on('chat:messageDeleted', handleDeleted);
    setWsConnected(chatSocket.connected);

    return () => {
      chatSocket.off('connected', handleReady);
      chatSocket.off('disconnect', handleDown);
      chatSocket.off('connect_error', handleDown);
      chatSocket.off('chat:message', handleNewMessage);
      chatSocket.off('newDM', handleNewDM);
      chatSocket.off('chat:typing', handleTyping);
      chatSocket.off('chat:messageEdited', handleEdited);
      chatSocket.off('chat:messageDeleted', handleDeleted);
      clearTimeout(handleTyping._clearTimer);
    };
  }, [currentUserId, markRoomRead, refreshRoomsSoon, t]);

  // Resolves true once the message is in, false when it is not: the composer then gives the text back.
  const handleSendMessage = async (content) => {
    if (!selectedRoom || !content.trim()) return false;
    const roomId = selectedRoom.id;
    setSending(true);
    try {
      const msg = await sendRoomMessage(roomId, { content });
      // The agent may have opened another conversation while this was on its way.
      if (selectedRoomRef.current?.id === roomId) {
        setMessages((prev) => mergeMessages(prev, [messageToViewShape(msg, currentUserId, t)]));
      }
      refreshRoomsSoon();
      return true;
    } catch (err) {
      console.error('[DirectMessaging] Failed to send message:', err);
      toast.error(t('messaging.sendMessageFailed'));
      return false;
    } finally {
      setSending(false);
    }
  };

  // Back to the list. A phone's own Back button asks for this too (see useThreadHistory), so it
  // has to be safe with nothing open: an entry left behind by an earlier visit.
  const closeThread = useCallback(() => {
    const open = selectedRoomRef.current;
    if (!open) return;
    chatSocket.leaveRoom(open.id);
    setSelectedRoom(null);
    setMessagesLoading(false);
    setIsTyping(false);
    fetchRooms();
  }, [fetchRooms]);
  const threadHistory = useThreadHistory('chatRoom', closeThread);

  const handleSelectRoom = (room) => {
    // The conversation that is already open stays as it is (and where it was scrolled to).
    if (selectedRoom && selectedRoom.id === room.id) return;
    if (selectedRoom) chatSocket.leaveRoom(selectedRoom.id);
    setSelectedRoom(room);
    // The new conversation starts empty and loading in this very render, not as the last one's
    // messages for a frame.
    setMessages([]);
    setNextCursor(null);
    setMessagesFailed(false);
    setMessagesLoading(true);
    setIsTyping(false);
    chatSocket.joinRoom(room.id);
    // On a phone the thread is a screen of its own: it takes a history entry, so Back closes it.
    threadHistory.opened();
  };

  const handleTypingInput = () => {
    if (selectedRoom) chatSocket.emitTyping(selectedRoom.id);
  };

  const visibleRooms = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return rooms;
    return rooms.filter((r) => `${r.participantName} ${r.lastMessage}`.toLowerCase().includes(query));
  }, [rooms, searchQuery]);

  // The open conversation as the newest room list knows it (so its header follows who is online),
  // else as it was when it was opened.
  const openRoom = selectedRoom ? (rooms.find((r) => r.id === selectedRoom.id) || selectedRoom) : null;

  let listBody;
  if (loading && rooms.length === 0) {
    listBody = <LoadingState skeleton="list" rows={6} message={t('messaging.loading')} />;
  } else if (listError && rooms.length === 0) {
    listBody = (
      <PageEmptyState
        variant="error"
        size="lg"
        title={t('messaging.loadConversationsFailed')}
        onRetry={fetchRooms}
      />
    );
  } else if (rooms.length === 0) {
    listBody = (
      <PageEmptyState
        variant="inbox"
        size="lg"
        title={t('messaging.noConversationsYet')}
        description={t('messaging.noConversationsHint')}
      />
    );
  } else if (visibleRooms.length === 0) {
    listBody = (
      <InlineEmpty
        kind="search"
        title={t('messaging.noResults')}
        description={t('messaging.noResultsHint')}
      />
    );
  } else {
    listBody = (
      <MessageConversationList
        conversations={visibleRooms}
        activeId={selectedRoom?.id}
        onSelectConversation={handleSelectRoom}
      />
    );
  }

  // The first answer is still on its way: the right pane waits too, so a "select a conversation"
  // state is not drawn for a frame and taken away again.
  const firstLoad = loading && rooms.length === 0;
  // Nothing to choose from (no conversation yet, or the list would not load): one state across the
  // whole card, not an empty list beside a "select a conversation" pane and a search box for nothing.
  const listOnly = !loading && rooms.length === 0 && !selectedRoom;

  return (
    <div className={`dm-shell section-card ui-split${selectedRoom ? ' has-thread' : ''}${listOnly ? ' is-list-only' : ''}`}>
      {/* Conversation list */}
      <section className="dm-list-pane">
        {!listOnly && (
          <div className="dm-list-head">
            <SearchInput
              className="dm-search"
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder={t('messaging.searchPlaceholder')}
            />
          </div>
        )}

        {showOffline && (
          <div className="dm-live" role="status">
            <span className="dm-live__dot" aria-hidden="true" />
            {t('messaging.reconnecting')}
          </div>
        )}

        <div className="dm-list-scroll" aria-busy={loading}>{listBody}</div>
      </section>

      {/* Open thread */}
      {!listOnly && (
        <section className="dm-detail-pane">
          {selectedRoom ? (
            <MessageDetailView
              key={selectedRoom.id}
              conversation={openRoom}
              messages={messages}
              onBack={threadHistory.back}
              onSendMessage={handleSendMessage}
              onTyping={handleTypingInput}
              sending={sending}
              isTyping={isTyping}
              hasMore={Boolean(nextCursor)}
              loadingMore={loadingOlder}
              onLoadMore={handleLoadOlder}
              loading={messagesLoading}
              loadFailed={messagesFailed}
              onRetry={() => fetchMessages(selectedRoom.id)}
            />
          ) : !firstLoad && (
            <PageEmptyState
              size="lg"
              kind="messages"
              title={t('messaging.selectConversation')}
              description={t('messaging.selectConversationHint')}
            />
          )}
        </section>
      )}
    </div>
  );
}

export default DirectMessaging;
